import React, { useCallback, useMemo, useState } from 'react';
import Dialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import EDCheckbox from '../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import { AnalysTypeEnum } from '../../Const/EkonomiskAnalysConsts';
import {
  getLineChartLabel,
  getPieChartLabel,
} from '../../Utils/detaljanalysUtils';
import { TABLE_DISPLAY_LABEL } from './Consts/ekonomiskAnalysSammanfattningConts';

interface IProps {
  analysType: AnalysTypeEnum;
  open: boolean;
  onClose: () => void;
  onPrint: (selectedSections: IPrintSections) => void;
  showFordelningDiagram: boolean;
  showResultatDiagramSection: boolean;
}

export interface IPrintSections {
  sammanfattning: boolean;
  resultatdiagram: boolean;
  stapeldiagram: boolean;
  linjediagram: boolean;
  cirkeldiagram: boolean;
  tabell: boolean;
  aiInsikter: boolean;
}

const DetaljanalysPrintModal: React.FC<IProps> = ({
  analysType,
  open,
  onClose,
  onPrint,
  showFordelningDiagram,
  showResultatDiagramSection,
}) => {
  const [selectedSections, setSelectedSections] = useState<IPrintSections>({
    sammanfattning: true,
    resultatdiagram: true,
    stapeldiagram: true,
    linjediagram: true,
    cirkeldiagram: true,
    tabell: true,
    aiInsikter: true,
  });

  const handleCheckboxChange =
    (section: keyof IPrintSections) => (checked: boolean) => {
      setSelectedSections(prev => ({
        ...prev,
        [section]: checked,
      }));
    };

  const handleSelectAll = useCallback(() => {
    const allSelected = Object.values(selectedSections).every(value => value);
    const newValue = !allSelected;

    setSelectedSections({
      sammanfattning: newValue,
      resultatdiagram: newValue,
      stapeldiagram: newValue,
      linjediagram: newValue,
      cirkeldiagram: newValue,
      tabell: newValue,
      aiInsikter: newValue,
    });
  }, [selectedSections]);

  const handlePrint = useCallback(() => {
    onPrint(selectedSections);
  }, [onPrint, selectedSections]);

  const allSelected = Object.values(selectedSections).every(value => value);
  const noneSelected = Object.values(selectedSections).every(value => !value);

  const footerActions = useMemo(() => {
    const avbryt = { label: 'Avbryt', onClick: onClose };
    const skrivUt = {
      label: 'Skriv ut',
      onClick: handlePrint,
      buttonProps: {
        disabled: noneSelected,
      },
    };
    const markeraAlla = {
      label: allSelected ? 'Avmarkera alla' : 'Markera alla',
      onClick: handleSelectAll,
    };

    return [markeraAlla, avbryt, skrivUt];
  }, [allSelected, handlePrint, handleSelectAll, noneSelected, onClose]);

  return (
    <Dialog
      title="Skriv ut detaljanalys"
      open={open}
      onClose={onClose}
      footerActions={footerActions}
      maxWidth="sm"
      fullWidth
    >
      <EDBox>
        <EDBox marginTop={2}>
          <EDCheckbox
            label="Sammanfattning"
            checked={selectedSections.sammanfattning}
            onCheck={handleCheckboxChange('sammanfattning')}
            checkboxSpacing={{ paddingY: 0.5 }}
          />
          {showResultatDiagramSection && (
            <EDCheckbox
              label="Utgående resultat per period"
              checked={selectedSections.resultatdiagram}
              onCheck={handleCheckboxChange('resultatdiagram')}
              checkboxSpacing={{ paddingY: 0.5 }}
            />
          )}
          <EDCheckbox
            label={getLineChartLabel(analysType)}
            checked={selectedSections.linjediagram}
            onCheck={handleCheckboxChange('linjediagram')}
            checkboxSpacing={{ paddingY: 0.5 }}
          />
          {showFordelningDiagram && (
            <EDCheckbox
              label={getPieChartLabel(analysType)}
              checked={selectedSections.cirkeldiagram}
              onCheck={handleCheckboxChange('cirkeldiagram')}
              checkboxSpacing={{ paddingY: 0.5 }}
            />
          )}
          <EDCheckbox
            label="Utfall mot budget"
            checked={selectedSections.stapeldiagram}
            onCheck={handleCheckboxChange('stapeldiagram')}
            checkboxSpacing={{ paddingY: 0.5 }}
          />
          <EDCheckbox
            label={TABLE_DISPLAY_LABEL}
            checked={selectedSections.tabell}
            onCheck={handleCheckboxChange('tabell')}
            checkboxSpacing={{ paddingY: 0.5 }}
          />
          <EDCheckbox
            label="AI-insikter & rekommendationer"
            checked={selectedSections.aiInsikter}
            onCheck={handleCheckboxChange('aiInsikter')}
            checkboxSpacing={{ paddingY: 0.5 }}
          />
        </EDBox>
      </EDBox>
    </Dialog>
  );
};

export default DetaljanalysPrintModal;
